import React, { useState } from 'react';

const NBA_TEAMS = [
  'Bulls', 'Lakers', 'Celtics', 'Warriors', 'Spurs',
  'Heat', 'Nuggets', 'Bucks', 'Suns', 'Nets',
  'Knicks', 'Clippers', 'Mavericks', 'Thunder', 'Pistons'
];

const API_URL = process.env.REACT_APP_API_URL;

interface SimResult {
  team1:               string;
  team2:               string;
  win_probability:     number;
  avg_games:           number;
  simulations:         number;
  confidence_interval: number;
  t1_strength?:        number;
  t2_strength?:        number;
}

const safe = (val: any, fallback: number = 0): number => {
  const n = Number(val);
  return isNaN(n) ? fallback : n;
};

const Simulate: React.FC = () => {
  const [team1,       setTeam1]    = useState('Bulls');
  const [team2,       setTeam2]    = useState('Lakers');
  const [runs,        setRuns]     = useState(10000);
  const [result,      setResult]   = useState<SimResult | null>(null);
  const [analysis,    setAnalysis] = useState('');
  const [loading,     setLoading]  = useState(false);
  const [history,     setHistory]  = useState<SimResult[]>([]);

  const runSim = async () => {
    if (team1 === team2 || loading) return;
    setLoading(true);
    setResult(null);
    setAnalysis('');
    try {
      const simRes  = await fetch(`${API_URL}/simulate`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ team1, team2, runs })
      });
      const simData = await simRes.json();
      const parsed: SimResult = {
        team1:               simData.team1 || team1,
        team2:               simData.team2 || team2,
        win_probability:     safe(simData.win_probability, 50),
        avg_games:           safe(simData.avg_games, 0),
        simulations:         safe(simData.simulations, runs),
        confidence_interval: safe(simData.confidence_interval, 0),
        t1_strength:         safe(simData.t1_strength, 0.50),
        t2_strength:         safe(simData.t2_strength, 0.50),
      };
      setResult(parsed);
      setHistory(prev => [parsed, ...prev].slice(0, 5));

      const chatRes  = await fetch(`${API_URL}/chat`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          prompt: `Playoff simulation: ${team1} wins ${parsed.win_probability}% of ${parsed.simulations} simulations vs ${team2}. Average series: ${parsed.avg_games} games. Brief narrative analysis.`
        })
      });
      const chatData = await chatRes.json();
      setAnalysis(chatData.response || '');
    } catch (err) {
      setAnalysis('Connection error. Check your API endpoint.');
    } finally {
      setLoading(false);
    }
  };

  const t1pct = result ? safe(result.win_probability, 50) : 50;
  const t2pct = 100 - t1pct;

  return (
    <div style={{ background: '#0D0F14', minHeight: 'calc(100vh - 48px)', color: '#F0EDE6', padding: '20px 24px', overflowY: 'auto' }}>

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 22, color: '#F5820A', margin: 0 }}>Playoff Simulator</h1>
        <p style={{ fontFamily: 'sans-serif', fontSize: 12, color: '#8892A4', margin: '4px 0 0' }}>
          Monte Carlo engine — up to 10,000 simulations per matchup
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20 }}>

        {/* Controls */}
        <div>
          <div style={{ background: '#13161D', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 20 }}>
            {[
              { label: 'Team 1', val: team1, set: setTeam1, color: '#F5820A' },
              { label: 'Team 2', val: team2, set: setTeam2, color: '#7B8FD4' }
            ].map(t => (
              <div key={t.label} style={{ marginBottom: 16 }}>
                <label style={{ fontFamily: 'Courier New', fontSize: 9, color: t.color, letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                  {t.label}
                </label>
                <select value={t.val} onChange={e => t.set(e.target.value)} style={{
                  width: '100%', background: '#1C2030', border: `0.5px solid ${t.color}44`,
                  borderRadius: 8, padding: '9px 12px', color: '#F0EDE6',
                  fontSize: 13, fontFamily: 'sans-serif', outline: 'none', cursor: 'pointer'
                }}>
                  {NBA_TEAMS.map(tm => <option key={tm} value={tm}>{tm}</option>)}
                </select>
              </div>
            ))}

            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  Simulations
                </label>
                <span style={{ fontFamily: 'Courier New', fontSize: 11, color: '#F0EDE6' }}>
                  {runs.toLocaleString()}
                </span>
              </div>
              <input type="range" min={1000} max={10000} step={1000} value={runs}
                onChange={e => setRuns(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#F5820A' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4' }}>1K</span>
                <span style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4' }}>10K</span>
              </div>
            </div>

            <button onClick={runSim} disabled={loading || team1 === team2} style={{
              width: '100%', background: loading ? '#333' : '#F5820A',
              border: 'none', borderRadius: 8, padding: '12px',
              color: '#fff', fontFamily: 'Courier New', fontSize: 11,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              cursor: loading || team1 === team2 ? 'not-allowed' : 'pointer'
            }}>
              {loading ? 'Running Simulations...' : `▶ Run ${runs.toLocaleString()} Simulations`}
            </button>

            {team1 === team2 && (
              <p style={{ fontFamily: 'sans-serif', fontSize: 11, color: '#E74C3C', textAlign: 'center', marginTop: 8 }}>
                Select two different teams
              </p>
            )}
          </div>

          {/* History */}
          {history.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                Recent Simulations
              </div>
              {history.map((h, i) => (
                <div key={i} style={{
                  background: '#13161D', border: '0.5px solid rgba(255,255,255,0.06)',
                  borderRadius: 8, padding: '8px 12px', marginBottom: 6,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <span style={{ fontFamily: 'sans-serif', fontSize: 11, color: '#8892A4' }}>
                    {h.team1} vs {h.team2}
                  </span>
                  <span style={{ fontFamily: 'Courier New', fontSize: 11, color: '#F5820A' }}>
                    {safe(h.win_probability, 50).toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Results */}
        <div>
          <div style={{ background: '#13161D', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: 18, color: '#F5820A' }}>{team1}</div>
                <div style={{ fontFamily: 'Courier New', fontSize: 24, fontWeight: 700, color: '#F5820A', marginTop: 4 }}>
                  {t1pct.toFixed(1)}%
                </div>
                <div style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4' }}>WIN PROBABILITY</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ fontFamily: 'Courier New', fontSize: 11, color: '#8892A4' }}>VS</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: 18, color: '#7B8FD4' }}>{team2}</div>
                <div style={{ fontFamily: 'Courier New', fontSize: 24, fontWeight: 700, color: '#7B8FD4', marginTop: 4 }}>
                  {t2pct.toFixed(1)}%
                </div>
                <div style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4' }}>WIN PROBABILITY</div>
              </div>
            </div>

            <div style={{ height: 10, borderRadius: 5, display: 'flex', overflow: 'hidden', background: 'rgba(255,255,255,0.06)' }}>
              <div style={{ width: `${t1pct}%`, background: '#F5820A', transition: 'width 0.8s ease', borderRadius: '5px 0 0 5px' }} />
              <div style={{ width: `${t2pct}%`, background: '#7B8FD4', transition: 'width 0.8s ease', borderRadius: '0 5px 5px 0' }} />
            </div>

            {result && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 16 }}>
                {[
                  { label: 'Avg Series Length', val: `${safe(result.avg_games).toFixed(1)} games` },
                  { label: 'Simulations Run',   val: safe(result.simulations, runs).toLocaleString() },
                  { label: 'Confidence ±',      val: `${safe(result.confidence_interval).toFixed(1)}%` }
                ].map(s => (
                  <div key={s.label} style={{ background: '#1C2030', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'Courier New', fontSize: 14, color: '#F0EDE6' }}>{s.val}</div>
                    <div style={{ fontFamily: 'sans-serif', fontSize: 10, color: '#8892A4', marginTop: 3 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(analysis || loading) && (
            <div style={{ background: '#13161D', border: '0.5px solid rgba(245,130,10,0.3)', borderRadius: 10, padding: 18 }}>
              <div style={{ fontFamily: 'Courier New', fontSize: 9, color: '#F5820A', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
                ◉ Series Analysis
              </div>
              {loading
                ? <div style={{ fontFamily: 'sans-serif', fontSize: 13, color: '#8892A4' }}>Generating narrative...</div>
                : <div style={{ fontFamily: 'sans-serif', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{analysis}</div>
              }
            </div>
          )}

          {!result && !loading && (
            <div style={{ background: '#13161D', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🏀</div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 16, color: '#8892A4' }}>Select two teams and run a simulation</div>
              <div style={{ fontFamily: 'sans-serif', fontSize: 12, color: '#8892A4', marginTop: 6 }}>Monte Carlo engine runs up to 10,000 playoff series</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Simulate;